import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import EmployerCalculator from '../components/EmployerCalculator';
import {
  ArrowRight, ArrowDown, X, Loader2,
  Shield, AlertTriangle, Store, Ticket, Banknote,
  Settings2, Users, Globe, Building2,
  Landmark, Clock, ChevronDown, ChevronUp,
  CheckCircle, XCircle,
  FileText, BarChart3,
} from 'lucide-react';

function FadeIn({ children, delay = 0, className = '' }) {
  return (
    <div className={`animate-in fade-in slide-in-from-bottom-4 ${className}`} style={{ animationDelay: `${delay}s`, animationFillMode: 'both', animationDuration: '0.6s' }}>
      {children}
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#e5e7eb] last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group" data-testid={`faq-${q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}>
        <span className="text-base font-semibold text-[#1a1d1f] pr-4 group-hover:text-[#D64D7A] transition-colors" style={{ fontFamily: 'Manrope' }}>{q}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#9ca3af] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#9ca3af] flex-shrink-0" />}
      </button>
      {open && <div className="pb-5 text-sm text-[#6b7280] leading-relaxed">{a}</div>}
    </div>
  );
}

export default function ForEmployersPage() {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', company_name: '', phone: '', company_size: '', role: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const openDemo = () => { setFormError(''); setShowDemo(true); };
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name || !formData.email || !formData.company_name || !formData.phone || !formData.company_size || !formData.role) {
      setFormError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'for_employers_private' }),
      });
    } catch (_) {}
    setSubmitting(false);
    setShowDemo(false);
    const name = formData.name;
    const email = formData.email;
    setFormData({ name: '', email: '', company_name: '', phone: '', company_size: '', role: '' });
    const params = new URLSearchParams({ name, email });
    window.open(`https://calendly.com/hello-thcohqq?${params.toString()}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="for-employers-page">

      {/* DEMO MODAL */}
      {showDemo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="demo-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDemo(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-7 animate-in fade-in zoom-in-95">
            <button onClick={() => setShowDemo(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#f9fafb] hover:bg-[#f0f0f0] flex items-center justify-center transition-colors" data-testid="demo-modal-close">
              <X className="w-4 h-4 text-[#6b7280]" />
            </button>
            <div className="mb-6">
              <img src="/pebbles-logo.svg" alt="Pebbles" className="h-6 mb-4" />
              <h3 className="text-xl font-extrabold text-[#1a1d1f]" style={{ fontFamily: 'Manrope' }}>Talk to us</h3>
              <p className="text-sm text-[#6b7280] mt-1">We'll model your specific salary bands and walk you through how it works.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Full Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Funke Adeyemi"
                  className="w-full h-10 px-3.5 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all" data-testid="demo-name" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Work Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="funke@company.ng"
                  className="w-full h-10 px-3.5 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all" data-testid="demo-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Company Name</label>
                  <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="Acme Corp"
                    className="w-full h-10 px-3.5 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all" data-testid="demo-company" />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Phone Number</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+234 801 234 5678"
                    className="w-full h-10 px-3.5 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] placeholder:text-[#c4c8cc] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all" data-testid="demo-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Company Size</label>
                  <select value={formData.company_size} onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] appearance-none" data-testid="demo-size">
                    <option value="" disabled>Select</option>
                    <option value="1-10">1 - 10</option>
                    <option value="11-50">11 - 50</option>
                    <option value="51-200">51 - 200</option>
                    <option value="201-500">201 - 500</option>
                    <option value="500+">500+</option>
                  </select>
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-[#374151] mb-1 block">Your Role</label>
                  <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg border border-[#e5e7eb] bg-white text-sm text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] appearance-none" data-testid="demo-role">
                    <option value="" disabled>Select</option>
                    <option value="founder_ceo">Founder / CEO</option>
                    <option value="hr_lead">HR Lead / People Ops</option>
                    <option value="cfo_finance">CFO / Finance</option>
                    <option value="operations">Operations</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              {formError && <p className="text-xs text-red-500 font-medium" data-testid="demo-form-error">{formError}</p>}
              <Button type="submit" disabled={submitting} className="w-full bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-xl h-11 text-sm font-semibold shadow-[0_4px_20px_rgba(214,77,122,0.25)] mt-2" data-testid="demo-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <>Book a Demo <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
              <p className="text-[11px] text-[#9ca3af] text-center">You'll be redirected to our calendar to pick a time.</p>
            </form>
          </div>
        </div>
      )}

      {/* NAV */}
      <nav className="bg-white border-b border-[#f0f0f0] sticky top-0 z-50" data-testid="for-employers-nav">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-14 px-5 lg:px-8">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group">
            <img src="/pebbles-logo.svg" alt="Pebbles" className="h-7 group-hover:scale-105 transition-transform" />
          </button>
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-semibold text-[#D64D7A] bg-[#D64D7A]/10 px-2.5 py-1 rounded-full mr-2">Sales Page</span>
            <Button variant="outline" className="rounded-lg text-[13px] font-semibold border-[#e5e7eb] h-9 px-4" onClick={() => navigate('/login')}>Log In</Button>
            <Button className="rounded-lg text-[13px] font-semibold bg-[#D64D7A] hover:bg-[#c4406b] text-white h-9 px-4" onClick={openDemo} data-testid="nav-demo-btn">Talk to Us</Button>
          </div>
        </div>
      </nav>

      {/* SECTION 1: Hero */}
      <section className="bg-[#fafafa] border-b border-[#f0f0f0]" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <FadeIn>
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1a1d1f] tracking-tight leading-[1.1]" style={{ fontFamily: 'Manrope' }} data-testid="hero-headline">
                You're already spending the money. Your team should feel it.
              </h1>
              <p className="text-base lg:text-lg text-[#6b7280] mt-6 max-w-2xl leading-relaxed">
                Right now your ₦X goes into an account and your team figures out the rest. When you provide meals, transport, health cover, and pension through Pebbles — delivered through vendors, tracked, compliant — your team gets a proper benefits package. Your spend stays the same.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button size="lg" className="bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-xl h-13 px-7 text-base font-semibold shadow-[0_4px_20px_rgba(214,77,122,0.25)]" onClick={() => scrollTo('estimator-section')} data-testid="hero-see-action-btn">
                  See It In Action <ArrowDown className="ml-2 w-4 h-4" />
                </Button>
                <Button size="lg" variant="outline" className="border-[#d1d5db] text-[#374151] hover:bg-[#f9fafb] rounded-xl h-13 px-7 text-base font-semibold" onClick={openDemo} data-testid="hero-talk-btn">
                  Talk to Us
                </Button>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 2: Interactive Employer Calculator */}
      <EmployerCalculator openDemo={openDemo} />

      {/* SECTION 3: Tax Regime Recognises Structured Benefits */}
      <section className="bg-[#fafafa] border-y border-[#f0f0f0] py-20 lg:py-24" data-testid="tax-regime-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="max-w-3xl mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>The new tax regime increases incentives for structured benefits and raises the cost of payroll errors</h2>
              <p className="text-base text-[#6b7280] mt-4 leading-relaxed">
                The Nigeria Tax Act 2025 (effective January 2026) introduces specific provisions that recognise properly delivered employee benefits and reward companies that create jobs. Here are the key provisions most employers are missing.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                tag: 'Company Tax Benefit', tagColor: '#6366f1',
                title: 'Compensation Relief',
                subtitle: '50% additional deduction against your company\'s assessable profits on transport subsidies and wage awards to workers earning under ₦100,000/month gross',
                desc: 'When you provide transport subsidies or wage increases to workers earning under ₦100,000 gross monthly, you can claim a 50% additional deduction on that cost against your company\'s assessable profits. This is a Company Income Tax benefit. It reduces your corporate tax bill — it does not affect employee PAYE.',
                cite: 'NTA 2025 — Compensation Relief. Note: applies to qualifying costs incurred within specified calendar years and for workers below the income threshold. Conditions apply.',
              },
              {
                tag: 'Company Tax Benefit', tagColor: '#6366f1',
                title: 'Employment Relief',
                subtitle: '50% CIT deduction on salaries of net-new hires retained for 3+ years',
                desc: 'When you create net-new positions (above your prior year headcount) and retain those employees for at least three years, you can claim a 50% deduction on their salaries against assessable profits. This is a Company Income Tax benefit. It reduces your corporate tax bill — it does not affect employee PAYE. Pebbles tracks employee tenure automatically — your documentation for this claim is built in.',
                cite: 'NTA 2025 — Employment Relief. Note: applies to net increase in employee count over prior years. Time-limited provision — verify current applicability with your tax advisor.',
              },
              {
                tag: 'Employee Tax Benefit', tagColor: '#00B894',
                title: 'Work Tools Exemption',
                subtitle: 'Company-provided devices and equipment — generally not treated as taxable benefits-in-kind for the employee when reasonably required for their role',
                desc: 'Laptops, phones, and specialised equipment provided to employees for work are generally treated as non-taxable benefits-in-kind, provided the tools are reasonably required for the employee\'s role. Provide them through Pebbles and the record-keeping is automatic.',
                cite: 'NTA 2025 — Section 14(3). Note: tools must be appropriate to the employee\'s job function.',
              },
              {
                tag: 'Employee Tax Benefit', tagColor: '#00B894',
                title: 'Housing Benefit Cap',
                subtitle: 'Housing benefits capped at 20% of gross for tax purposes',
                desc: 'If you provide accommodation for employees, the taxable value is capped at 20% of their annual gross income — even if the actual value is higher. This protects both you and your employee from unexpected tax exposure.',
                cite: 'NTA 2025 — Section 14(6)',
              },
            ].map((card, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className="bg-white rounded-xl border border-[#e5e7eb] p-7 h-full" data-testid={`tax-card-${i}`}>
                  <span className="inline-block text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-4" style={{ backgroundColor: `${card.tagColor}12`, color: card.tagColor }}>{card.tag}</span>
                  <h3 className="text-lg font-bold text-[#1a1d1f] mb-1" style={{ fontFamily: 'Manrope' }}>{card.title}</h3>
                  <p className="text-sm font-semibold text-[#374151] mb-3">{card.subtitle}</p>
                  <p className="text-sm text-[#6b7280] leading-relaxed mb-4">{card.desc}</p>
                  <p className="text-[11px] text-[#9ca3af] italic leading-relaxed">{card.cite}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: Penalties */}
      <section className="bg-[#1a1d1f] text-white py-20 lg:py-24" data-testid="penalties-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="max-w-3xl mb-14">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-[#f59e0b]" />
                <span className="text-xs font-bold text-[#f59e0b] uppercase tracking-wider">Compliance Risk</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope' }}>The NTA 2025 doesn't just recognise compliance. It penalises the lack of it.</h2>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 gap-5 mb-10">
            {[
              {
                penalty: '40% administrative penalty',
                desc: 'on any PAYE amount you fail to deduct from an employee\'s salary. Not a fine — 40% of the actual tax you should have withheld.',
                cite: 'NTAA 2025, Section 105',
              },
              {
                penalty: '10% penalty + interest at CBN MPR',
                desc: 'on any PAYE you deduct but remit late. Remittance is due by the 21st of the month following deduction. Miss it, and it compounds.',
                cite: 'NTAA 2025, Section 107',
              },
              {
                penalty: '₦100,000 first month + ₦50,000/month',
                desc: 'for late or missing annual PAYE returns (Form H1). Due by January 31 every year.',
                cite: 'NTAA 2025, Section 101',
              },
              {
                penalty: '₦5,000,000 penalty',
                desc: 'for engaging unregistered contractors without proper tax documentation.',
                cite: 'NTAA 2025, Section 100(2)',
              },
            ].map((item, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className="bg-white/[0.05] border border-white/10 rounded-xl p-6" data-testid={`penalty-${i}`}>
                  <p className="text-lg font-extrabold text-[#ef4444] mb-2" style={{ fontFamily: 'Manrope' }}>{item.penalty}</p>
                  <p className="text-sm text-[#d1d5db] leading-relaxed mb-3">{item.desc}</p>
                  <p className="text-[11px] text-[#6b7280] italic">{item.cite}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.3}>
            <div className="bg-white/[0.05] border border-white/10 rounded-xl p-6 max-w-3xl">
              <p className="text-sm text-[#d1d5db] leading-relaxed">
                The Nigeria Revenue Service (NRS) and state revenue authorities increasingly cross-reference payroll filings, remittances, and third-party bank records. Discrepancies between what you transfer to employees and what you file as PAYE are now easier to detect. <span className="text-white font-semibold">Pebbles calculates PAYE using NTA 2025 bands, deducts correctly every cycle, remits before the deadline, and keeps audit-ready records for every employee, every month.</span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* SECTION 6: How Benefits Are Delivered */}
      <section className="bg-[#fafafa] border-y border-[#f0f0f0] py-20 lg:py-24" data-testid="delivery-legal-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="max-w-2xl mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>How you deliver benefits determines how they're treated.</h2>
              <p className="text-base text-[#6b7280] mt-4 leading-relaxed">
                Under Nigerian tax law, the form of a benefit matters as much as the amount. The same ₦30,000 can be a company-provided benefit or taxable pay — depending entirely on how you deliver it.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Store, color: '#00B894', bg: 'bg-[#00B894]/8', good: true,
                title: 'Vendor-provided benefits',
                top: 'Generally treated more favourably when structured correctly and documented.',
                example: 'Company contracts a vendor to deliver meals to your team. Employees never touch cash.',
                pebbles: 'Pebbles routes payment directly to the vendor on your behalf.',
              },
              {
                icon: Ticket, color: '#6366f1', bg: 'bg-[#6366f1]/8', good: true,
                title: 'Vouchers',
                top: 'Generally not treated as taxable benefits-in-kind when properly issued to general staff and documented.',
                example: 'Company issues meal vouchers redeemable at your approved vendors. Employees use vouchers, not cash.',
                pebbles: 'Pebbles issues and tracks vouchers digitally through the platform.',
              },
              {
                icon: Banknote, color: '#ef4444', bg: 'bg-red-50', good: false,
                title: 'Cash allowances',
                top: 'Treated as taxable pay regardless of what you call it.',
                example: 'Company pays ₦30,000 "food allowance" into an employee\'s bank account.',
                pebbles: 'This is just salary with a different label. Pebbles doesn\'t do this.',
              },
            ].map((item, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className={`rounded-xl border p-7 h-full flex flex-col ${item.good ? 'bg-white border-[#e5e7eb]' : 'bg-[#fef2f2] border-red-100'}`} data-testid={`delivery-card-${i}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.bg}`}>
                      <item.icon className="w-5 h-5" style={{ color: item.color }} />
                    </div>
                    {item.good ? <CheckCircle className="w-4 h-4 text-[#00B894]" /> : <XCircle className="w-4 h-4 text-red-400" />}
                  </div>
                  <h3 className="text-base font-bold text-[#1a1d1f] mb-2" style={{ fontFamily: 'Manrope' }}>{item.title}</h3>
                  <p className={`text-xs font-medium italic mb-3 ${item.good ? 'text-[#00B894]' : 'text-red-400'}`}>{item.top}</p>
                  <p className="text-sm text-[#6b7280] leading-relaxed mb-3 flex-1">{item.example}</p>
                  <p className={`text-xs font-semibold ${item.good ? 'text-[#374151]' : 'text-red-500'}`}>{item.pebbles}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.2}>
            <p className="text-sm text-[#374151] mt-8 max-w-3xl leading-relaxed">
              Pebbles ensures every benefit you provide reaches your team in the form the law treats more favourably — through vendors and vouchers, not cash transfers.
            </p>
            <p className="text-[11px] text-[#9ca3af] mt-3 italic leading-relaxed max-w-3xl">
              Tax treatment of benefits depends on structure, documentation, delivery method, and compliance with NTA 2025. The classifications above reflect general practice — consult a qualified tax professional for your specific situation.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 7: Time You Get Back */}
      <section className="py-20 lg:py-24" data-testid="time-savings-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="max-w-2xl mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>One payroll run. Everything handled.</h2>
              <p className="text-base text-[#6b7280] mt-4 leading-relaxed">
                Managing benefits manually means paying 5 vendors, tracking allocations in spreadsheets, and hoping your PAYE calculations survive an audit. Pebbles replaces all of that with one dashboard.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden" data-testid="ops-comparison-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e5e7eb] bg-[#fafafa]">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-[#9ca3af] uppercase tracking-wider">Without Pebbles</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-[#00B894] uppercase tracking-wider">With Pebbles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Calculate PAYE manually for each employee', 'Auto-calculated using NTA 2025 bands'],
                      ['Pay vendors separately for food, HMO, transport', 'All vendor payments routed in one payroll run'],
                      ['Generate payslips in spreadsheets', 'Compliant payslips generated instantly'],
                      ['Track benefits in a separate system', 'Benefits, payroll, and wallets — one dashboard'],
                      ['Prepare annual PAYE returns (Form H1) manually', 'Audit-ready reports exported in one click'],
                      ['Risk late remittance penalties', 'Auto-remittance with deadline tracking'],
                      ['Employees don\'t see what you provide', 'Your team sees their full package — including your pension contribution'],
                      ['Figure out rent relief / CRA transition yourself', 'NTA 2025 personal relief handled automatically'],
                      ['Employer pension invisible to employees', 'Employees see the ₦35K/month you invest in their future'],
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-[#f0f0f0] last:border-0">
                        <td className="px-6 py-3.5 text-[#6b7280]">{row[0]}</td>
                        <td className="px-6 py-3.5 text-[#374151] font-medium">{row[1]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="text-sm text-[#374151] mt-6 max-w-3xl leading-relaxed">
              Your finance team stops chasing spreadsheets. Your HR team stops fielding payslip questions. Your employees stop wondering where their money went.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 8: Your Rules. Their Choices. */}
      <section className="bg-[#fafafa] border-y border-[#f0f0f0] py-20 lg:py-24" data-testid="employee-choice-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>Your rules. Their choices.</h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                icon: Settings2, color: '#D64D7A',
                title: 'You set the framework',
                desc: 'Which benefits are available, maximum allocations per benefit, approved vendors — you decide. Pebbles enforces your rules automatically.',
              },
              {
                icon: Users, color: '#00B894',
                title: 'They personalise within it',
                desc: 'Your team picks the benefits that matter to them — meals, transport, HMO level, device provision. Different employees, different needs, same framework.',
              },
              {
                icon: FileText, color: '#6366f1',
                title: 'Everything is tracked',
                desc: 'Every selection, every change, every allocation — logged, compliant, and reflected in payroll automatically. No manual reconciliation. No spreadsheets.',
              },
            ].map((card, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className="bg-white rounded-xl border border-[#e5e7eb] p-7 text-center" data-testid={`choice-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${card.color}12` }}>
                    <card.icon className="w-6 h-6" style={{ color: card.color }} />
                  </div>
                  <h3 className="text-base font-bold text-[#1a1d1f] mb-2" style={{ fontFamily: 'Manrope' }}>{card.title}</h3>
                  <p className="text-sm text-[#6b7280] leading-relaxed">{card.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.2}>
            <p className="text-center text-sm font-semibold text-[#374151] mt-8">You keep control. They get choice. Nobody chases paperwork.</p>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 9: Built for Nigerian Companies */}
      <section className="py-20 lg:py-24" data-testid="built-for-ng-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight" style={{ fontFamily: 'Manrope' }}>Not another foreign HR tool. Built here, for here.</h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { icon: Shield, text: 'NTA 2025 compliant from day one — new tax bands, rent relief (replacing abolished CRA), new penalty framework — all handled' },
              { icon: Landmark, text: 'Assets Microfinance Bank integration — employee wallets, virtual accounts, instant settlement' },
              { icon: Globe, text: 'Naira-native — no currency conversion, no foreign platform fees' },
              { icon: Building2, text: 'Multi-company support — manage subsidiaries from one dashboard' },
              { icon: Store, text: 'Works with your existing vendors — Shoprite, HMO providers, transport operators, any vendor you already use' },
              { icon: BarChart3, text: 'State PAYE handled — different state revenue authorities, same platform' },
              { icon: Clock, text: 'Onboard in an afternoon — CSV upload, wallets created instantly, first payroll same week' },
            ].map((item, i) => (
              <FadeIn key={i} delay={0.03 * (i + 1)}>
                <div className="flex items-start gap-3 bg-[#fafafa] rounded-lg border border-[#e5e7eb] p-5" data-testid={`built-ng-${i}`}>
                  <item.icon className="w-5 h-5 text-[#D64D7A] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#374151] leading-relaxed">{item.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10: Social Proof */}
      <section className="bg-[#fafafa] border-y border-[#f0f0f0] py-16 lg:py-20" data-testid="social-proof-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1a1d1f] text-center mb-10" style={{ fontFamily: 'Manrope' }}>Already trusted by Nigerian companies</h2>
          </FadeIn>
          <div className="flex flex-wrap justify-center gap-10 lg:gap-16">
            {[
              { val: '500+', label: 'Companies' },
              { val: '45,000+', label: 'Employees Onboarded' },
              { val: '70%', label: 'Employee Enrollment Rate' },
              { val: 'NTA 2025', label: 'Compliant' },
            ].map((stat, i) => (
              <FadeIn key={i} delay={0.05 * (i + 1)}>
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl font-extrabold text-[#D64D7A]" style={{ fontFamily: 'Manrope' }}>{stat.val}</p>
                  <p className="text-sm text-[#6b7280] mt-1">{stat.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11: Executive FAQ */}
      <section className="py-20 lg:py-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1a1d1f] tracking-tight mb-10" style={{ fontFamily: 'Manrope' }}>Questions your CFO will ask</h2>
          </FadeIn>
          <div className="border-t border-[#e5e7eb]">
            <FAQItem
              q={'"You say our cost doesn\'t change. Where is the Pebbles fee?"'}
              a={"Pebbles charges a transparent platform fee per active employee per month. When we say \u2018same spend,\u2019 we mean your gross compensation budget stays the same \u2014 the \u20A6500,000 you allocate to an employee doesn\u2019t change. The platform fee is a separate operational cost, like your payroll software or HMO admin fee. We\u2019ll share pricing in our first call."}
            />
            <FAQItem
              q={'"We already use an ERP (SAP, Oracle, Odoo). Do we have to switch?"'}
              a={"No. Pebbles sits alongside your existing ERP, not in place of it. We integrate with your payroll data \u2014 you continue running your HR system as-is. Most clients export a CSV from their ERP, upload it to Pebbles, and the rest is handled. For larger implementations, we can build a direct API integration."}
            />
            <FAQItem
              q={'"How does this affect our annual PAYE returns (Form H1)?"'}
              a={"It simplifies them. Pebbles maintains a complete, auditable record of every deduction, remittance, and benefit for every employee throughout the year. When Form H1 is due (January 31), you export the report in one click. Every number ties back to what was actually deducted and remitted."}
            />
            <FAQItem
              q={'"What happens if an employee doesn\'t use their meal or transport allocation?"'}
              a={"That depends on your company policy \u2014 and this is a product decision you make during setup. Options include: roll unused benefits to the next month, let them expire, or reallocate to other benefit categories. We\u2019ll help you configure the right approach during onboarding. Tax implications of each option vary \u2014 we flag this during setup so you can confirm with your tax advisor."}
            />
            <FAQItem
              q={'"Is our payroll data secure?"'}
              a={"Employee wallets and payment processing run through our integration with Assets Microfinance Bank, a CBN-licensed institution. Payroll data is encrypted in transit and at rest. Access is role-based \u2014 only authorised personnel in your organisation can view employee-level data. We can walk you through our full security architecture during the demo."}
            />
            <FAQItem
              q={'"How long does onboarding take?"'}
              a={"Most companies are live within a week. Upload your team list (CSV or manual entry), configure your benefits menu and vendor list, and run your first payroll. Average time from first call to first pay run is 5 business days. Average employee enrollment hits 70% within 30 days of launch."}
            />
            <FAQItem
              q={'"The CRA was abolished. How does Pebbles handle the new personal relief?"'}
              a={"Pebbles applies the NTA 2025 rent relief automatically \u2014 20% of annual rent paid, capped at \u20A6500,000/year. Employees enter their annual rent during onboarding. The old Consolidated Relief Allowance (CRA) is no longer applied. If you have employees who were relying on CRA, their PAYE may change \u2014 Pebbles calculates the correct amount under the new law from day one."}
            />
          </div>
        </div>
      </section>

      {/* SECTION 12: Final CTA */}
      <section className="bg-[#1a1d1f] py-20 lg:py-28" data-testid="final-cta-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>Your team's next payroll could do more than pay salaries.</h2>
            <p className="text-base text-[#9ca3af] mt-4 max-w-2xl mx-auto leading-relaxed">
              Same spend. Real benefits delivered through vendors. Full compliance under NTA 2025. Pension your team can actually see. And ₦28,000 more left in their pocket every month.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button size="lg" className="bg-[#D64D7A] hover:bg-[#c4406b] text-white rounded-xl h-13 px-8 text-base font-semibold shadow-[0_4px_20px_rgba(214,77,122,0.25)]" onClick={openDemo} data-testid="final-demo-btn">
                Book a Demo <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-xl h-13 px-8 text-base font-semibold" onClick={() => navigate('/estimator')} data-testid="final-estimator-btn">
                Try the Benefits Estimator
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#f0f0f0] py-8" data-testid="for-employers-footer">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <img src="/pebbles-logo.svg" alt="Pebbles" className="h-6 opacity-60" />
            <div className="flex items-center gap-4 text-sm text-[#9ca3af]">
              <button onClick={() => navigate('/')} className="hover:text-[#1a1d1f] transition-colors">Home</button>
              <button onClick={() => navigate('/employers')} className="hover:text-[#1a1d1f] transition-colors">Employers</button>
              <button onClick={() => navigate('/estimator')} className="hover:text-[#1a1d1f] transition-colors">Estimator</button>
            </div>
          </div>
          <p className="text-[11px] text-[#9ca3af] leading-relaxed max-w-4xl">
            The information on this page is for general guidance only and does not constitute tax, legal, or financial advice. Tax treatment of benefits depends on the form of delivery, documentation, employer configuration, and compliance with the Nigeria Tax Act 2025 and the Nigeria Tax Administration Act 2025. Benefits described on this page refer to goods and services provided directly by the employer through approved vendors and providers, not cash allowances. Tax classifications shown are based on general practice and may vary by circumstance. Employer incentives such as Compensation Relief and Employment Relief are Company Income Tax benefits that reduce assessable profits — they do not reduce employee PAYE. These are subject to specific conditions, income thresholds, and time limitations as defined in the Act. Pebbles Technologies Ltd does not provide tax advice. Consult a qualified tax professional for specific guidance on your company's obligations. All illustrative examples assume standard salary structures and standard deductions — actual figures vary by state, individual reliefs, and employer configuration.
          </p>
          <p className="text-[11px] text-[#c4c8cc] mt-3">Pebbles Technologies Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
